/** Wire-shape checks only. Receipt/scoring verification remains exclusively server-owned. */
const object = (v: any) => v !== null && typeof v === 'object' && !Array.isArray(v);
const text = (v: any) => typeof v === 'string' && v.length > 0;
const date = (v: any) => text(v) && Number.isFinite(Date.parse(v));
const revision = (v: any) => Number.isInteger(v) && v >= 0;
const strings = (v: any) => Array.isArray(v) && v.every(text);
const domains = ['competency', 'fitness', 'unclassified'];
function measurement(v: any): boolean {
  return object(v) && text(v.measurement_id) && text(v.unit_id) && text(v.explanation)
    && (v.status === 'observed' ? typeof v.raw_value === 'number' && Number.isFinite(v.raw_value)
      : ['missing', 'invalid', 'not_attempted'].includes(v.status) && v.raw_value === null)
    && (v.points == null || typeof v.points === 'number' && Number.isFinite(v.points))
    && (v.status === 'observed' || v.points == null);
}
function provenance(v: any): boolean {
  return object(v) && ['configuration_id', 'configuration_digest', 'assessment_instance_id', 'source', 'assessor_id', 'device_id'].every(k => text(v[k]))
    && revision(v.configuration_revision) && date(v.accepted_at);
}
export function validateEvidence(value: any, kind: 'timeline' | 'status', student: string, program: string): void {
  const fail = () => { throw new Error('Evidence could not be displayed because the response was incomplete or inconsistent. Read again or contact your supervisor.'); };
  if (!object(value) || value.student_id !== student || value.program_id !== program || !date(value.as_of)
    || !object(value.coverage) || value.coverage.discovery !== 'eventual_learner_index_consistent_source_reads'
    || typeof value.coverage.complete_discovery !== 'boolean' || value.coverage.concurrent_changes !== 'not_a_point_in_time_snapshot'
    || value.coverage.historical_gap !== undefined && typeof value.coverage.historical_gap !== 'boolean') fail();
  if (kind === 'timeline') {
    if (value.order !== 'observed_time_within_index_page' || !Array.isArray(value.items) || value.items.length > 25
      || value.next_cursor !== undefined && (!text(value.next_cursor) || value.next_cursor.length > 2048)
      || value.coverage.complete_discovery === Boolean(value.next_cursor)) fail();
    for (const item of value.items) {
      if (!object(item)) fail();
      if (item.evidence_state === 'unavailable') { if (!text(item.reason)) fail(); continue; }
      if (item.evidence_state !== 'verified' || !text(item.id) || !text(item.event_id) || !text(item.event_label)
        || !domains.includes(item.domain) || !date(item.observed_at) || !date(item.received_at) || !revision(item.record_version)
        || Date.parse(item.observed_at) > Date.parse(value.as_of) || !provenance(item.provenance)
        || !Array.isArray(item.measurements) || !item.measurements.length || !item.measurements.every(measurement)
        || !strings(item.quality_flags) || !Array.isArray(item.corrections)
        || !['additional_annotations_in_protected_audit', 'no_additional_annotation_versions'].includes(item.annotation_lineage)) fail();
      if (!item.corrections.every((c: any) => object(c) && revision(c.from_version) && c.to_version === c.from_version + 1 && text(c.reason) && text(c.proposed_by) && text(c.approved_by) && c.proposed_by !== c.approved_by)) fail();
    }
  } else {
    if (typeof value.status_available !== 'boolean' || !text(value.explanation) || !domains.every(d => Array.isArray(value[d]))) fail();
    if (!value.status_available) {
      if (value.attainment !== 'unknown' || domains.some(d => value[d].length)) fail();
      return;
    }
    if (!value.coverage.complete_discovery || value.attainment !== 'not_defined' || value.interpretation !== 'latest_verified_evidence_per_exact_configuration_event_measure') fail();
    for (const domain of domains) for (const item of value[domain]) {
      if (!object(item) || item.domain !== domain || !text(item.event_id) || !text(item.attempt_id) || !date(item.observed_at)
        || Date.parse(item.observed_at) > Date.parse(value.as_of) || !measurement(item.measurement)
        || item.measurement_id !== item.measurement.measurement_id || !provenance(item.configuration)) fail();
    }
  }
}
